import React, { useMemo, useState } from "react";
import api from "../../api/client";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

const emptyDraft = {
  setor: "",
  texto_original: "",
  responsavel: "",
  prazo: "",
  prioridade: "media",
  norma: "",
};

const priorityLabel = {
  baixa: "Baixa",
  media: "Média",
  alta: "Alta",
  critica: "Crítica",
};

export default function ManualActionPlanBuilder({ clients, existingActions = [], onSaved }) {
  const [clientId, setClientId] = useState("");
  const [planName, setPlanName] = useState("Plano de ação complementar");
  const [draft, setDraft] = useState(emptyDraft);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const sectorSuggestions = useMemo(() => Array.from(new Set(
    existingActions
      .filter((item) => !clientId || item.client_id === clientId)
      .map((item) => String(item.setor || "").trim())
      .filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, "pt-BR")), [existingActions, clientId]);

  const includeRow = () => {
    if (!clientId) return toast.error("Selecione o cliente antes de incluir ações");
    if (!draft.texto_original.trim()) return toast.error("Descreva a ação que precisa ser executada");

    setRows((current) => [...current, {
      _key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      texto_original: draft.texto_original.trim(),
      titulo_curto: "",
      setor: draft.setor.trim(),
      responsavel: draft.responsavel.trim(),
      prazo: draft.prazo || null,
      prioridade: draft.prioridade,
      norma: draft.norma.trim(),
      recorrencia: "nenhuma",
      observacoes_internas: "",
      source_locator: "Cadastro manual no CEO",
    }]);

    setDraft((current) => ({
      ...emptyDraft,
      setor: current.setor,
      prioridade: current.prioridade,
    }));
  };

  const removeRow = (key) => setRows((current) => current.filter((row) => row._key !== key));

  const savePlan = async () => {
    if (!clientId) return toast.error("Selecione o cliente");
    if (!rows.length) return toast.error("Inclua pelo menos uma ação");
    if (!planName.trim()) return toast.error("Informe um nome para o plano");

    setSaving(true);
    try {
      await api.post("/operations/manual-action-plans", {
        client_id: clientId,
        nome: planName.trim(),
        origem: "manual",
        source_revision: "",
        source_filename: "",
        source_document_hash: "",
        items: rows.map(({ _key, ...row }) => row),
      });
      toast.success(`${rows.length} ação(ões) incluída(s) no plano`);
      setRows([]);
      setDraft(emptyDraft);
      setPlanName("Plano de ação complementar");
      await onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar plano de ação");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="manual-action-plan-builder">
      <Card>
        <CardContent className="p-5 sm:p-6">
          <div className="mb-5">
            <div className="text-xs font-medium text-muted-foreground">Complementar o acompanhamento</div>
            <h2 className="mt-1 text-lg font-semibold">Incluir ações manualmente</h2>
            <p className="mt-1 text-sm text-muted-foreground">Use para acrescentar ações que não vieram do PGR ou que surgiram depois, sem alterar o plano importado.</p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Cliente *</Label>
              <Select value={clientId} onValueChange={(value) => { setClientId(value); setRows([]); }}>
                <SelectTrigger><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
                <SelectContent>{clients.map((client) => <SelectItem key={client.id} value={client.id}>{client.razao_social}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nome do plano</Label>
              <Input value={planName} onChange={(e) => setPlanName(e.target.value)} placeholder="Ex.: Plano complementar setembro/2026" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 sm:p-6">
          <div className="mb-4">
            <div className="text-xs font-medium text-muted-foreground">Nova linha</div>
            <h3 className="mt-1 font-semibold">Preencha e toque em + Incluir</h3>
          </div>

          <div className="grid gap-4 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-3">
              <Label>Setor / local</Label>
              <Input
                list="manual-action-sectors"
                value={draft.setor}
                onChange={(e) => setDraft((current) => ({ ...current, setor: e.target.value }))}
                placeholder="Ex.: Produção, Almoxarifado"
              />
              <datalist id="manual-action-sectors">{sectorSuggestions.map((sector) => <option key={sector} value={sector} />)}</datalist>
            </div>
            <div className="lg:col-span-5">
              <Label>Ação necessária *</Label>
              <Textarea
                rows={2}
                value={draft.texto_original}
                onChange={(e) => setDraft((current) => ({ ...current, texto_original: e.target.value }))}
                placeholder="Ex.: Instalar proteção fixa no ponto de transmissão..."
              />
            </div>
            <div className="lg:col-span-2">
              <Label>Responsável</Label>
              <Input value={draft.responsavel} onChange={(e) => setDraft((current) => ({ ...current, responsavel: e.target.value }))} placeholder="Nome ou função" />
            </div>
            <div className="lg:col-span-2">
              <Label>Prazo</Label>
              <Input type="date" value={draft.prazo} onChange={(e) => setDraft((current) => ({ ...current, prazo: e.target.value }))} />
            </div>
            <div className="lg:col-span-3">
              <Label>Prioridade</Label>
              <Select value={draft.prioridade} onValueChange={(value) => setDraft((current) => ({ ...current, prioridade: value }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="baixa">Baixa</SelectItem>
                  <SelectItem value="media">Média</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="critica">Crítica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="lg:col-span-5">
              <Label>Norma / referência</Label>
              <Input value={draft.norma} onChange={(e) => setDraft((current) => ({ ...current, norma: e.target.value }))} placeholder="Opcional. Ex.: NR-12, item 12.5.1" />
            </div>
            <div className="lg:col-span-4 lg:flex lg:justify-end">
              <Button type="button" className="w-full lg:w-auto" onClick={includeRow} disabled={!clientId}>
                <Plus className="size-4" /> Incluir
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-col gap-3 border-b border-border/70 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div>
              <h3 className="font-semibold">Ações que serão salvas</h3>
              <p className="mt-1 text-xs text-muted-foreground">Revise a tabela antes de salvar o plano.</p>
            </div>
            <Badge variant="secondary">{rows.length} ação(ões)</Badge>
          </div>

          {rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Nenhuma ação incluída ainda.</div>
          ) : (
            <>
              <div className="hidden overflow-x-auto lg:block">
                <table className="w-full min-w-[950px] text-sm" data-ui-table>
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left">Setor / local</th>
                      <th className="px-4 py-3 text-left">Ação</th>
                      <th className="px-4 py-3 text-left">Responsável</th>
                      <th className="px-4 py-3 text-left">Prazo</th>
                      <th className="px-4 py-3 text-left">Prioridade</th>
                      <th className="px-4 py-3 text-right">Remover</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/70">
                    {rows.map((row) => (
                      <tr key={row._key}>
                        <td className="px-4 py-3">{row.setor || "Geral"}</td>
                        <td className="max-w-xl px-4 py-3 font-medium">{row.texto_original}</td>
                        <td className="px-4 py-3 text-muted-foreground">{row.responsavel || "—"}</td>
                        <td className="px-4 py-3 font-mono text-xs">{row.prazo || "—"}</td>
                        <td className="px-4 py-3"><Badge variant={row.prioridade === "critica" ? "destructive" : "outline"}>{priorityLabel[row.prioridade]}</Badge></td>
                        <td className="px-4 py-3 text-right"><Button type="button" variant="ghost" size="sm" onClick={() => removeRow(row._key)} aria-label="Remover ação"><Trash2 className="size-4 text-destructive" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="divide-y divide-border/70 lg:hidden">
                {rows.map((row, index) => (
                  <div key={row._key} className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-xs text-muted-foreground">{row.setor || "Geral"} · Ação {index + 1}</div>
                        <div className="mt-1 text-sm font-medium">{row.texto_original}</div>
                        <div className="mt-2 text-xs text-muted-foreground">Responsável: {row.responsavel || "—"} · Prazo: {row.prazo || "—"}</div>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeRow(row._key)} aria-label="Remover ação"><Trash2 className="size-4 text-destructive" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="flex flex-col gap-3 border-t border-border/70 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="text-xs text-muted-foreground">O plano manual será complementar e aparecerá junto das demais ações do cliente.</div>
            <Button type="button" onClick={savePlan} disabled={saving || !rows.length}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Salvar plano com {rows.length} ação(ões)
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
